
import {
  computed, defineComponent, onBeforeUnmount, onMounted, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import Gleap from 'gleap';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import DotsProgress from '@/components/onboarding-v2/shared/DotsProgress.vue';
import ConnectStep from '@/components/onboarding-v2/steps/ConnectStep.vue';
import MigrationV5WaitStep from '@/components/onboarding-v2/steps/MigrationV5WaitStep.vue';
import AuthCompleteLayout from '@/views/layout/AuthCompleteLayout.vue';
import { Logout, UserState } from '@/composables/User';
import { showToastError, getErrorMessage } from '@/helpers';
import {
  fetchMigrationV5Status,
  notifyMigrationV5Ready,
} from '@/composables/migrationV5';

/**
 * Step descriptor table. We deliberately do NOT reuse `useOnboardingState`
 * here — it's tightly coupled to the 4-step new-shop onboarding (profile,
 * shop confirm, connect, lift-off) with persistence, validators and a
 * narrative-suffix engine we don't need. Migration V4 → V5 has only 2
 * states and they're driven entirely by the backend (connect → ready →
 * none). Local state is enough.
 */
const STEPS = [
  { id: 'connect', component: ConnectStep },
  { id: 'wait', component: MigrationV5WaitStep },
] as const;

/**
 * Body class toggled while this view is mounted. A non-scoped CSS rule
 * at the bottom of this SFC hides every automatic Gleap surface
 * (banners, modals, notifications, product tours) for as long as the
 * class is on `<body>`. Mirrors the pattern in `LayoutSubscription.vue`
 * — same problem: Gleap injects elements directly into `document.body`
 * outside the Vue component tree, so JS toggles like
 * `setDisableInAppNotifications(true)` race with the WebSocket push
 * and let banners slip through. CSS wins because it kicks in the
 * moment Gleap injects the node.
 */
const GLEAP_SILENT_BODY_CLASS = 'migration-v5-no-gleap-ui';

type StepId = (typeof STEPS)[number]['id'];

export default defineComponent({
  name: 'MigrationV5',

  components: {
    SpmButton,
    DotsProgress,
    ConnectStep,
    MigrationV5WaitStep,
    AuthCompleteLayout,
  },

  setup() {
    const { t } = useI18n();
    const router = useRouter();

    const currentIndex = ref(0);
    const totalSteps = STEPS.length;

    const currentStep = computed(() => STEPS[currentIndex.value]);
    const isFirst = computed(() => currentIndex.value === 0);
    const isLast = computed(() => currentIndex.value === STEPS.length - 1);

    /**
     * Re-entry handling: pick the right starting step from the backend
     * status. If the module is already connected (status has advanced to
     * `ready_to_migrate_data` / `data_migration_in_progress` /
     * `data_migrated`), land directly on the WAIT step — re-doing the
     * connect step would be confusing. Otherwise (`in_progress`) start
     * on the connect step. ConnectStep stays idempotent regardless.
     */
    onMounted(async () => {
      // Hide every automatic Gleap surface for as long as the migration
      // view is mounted. CSS-based, so it covers banners injected AFTER
      // mount too — which JS calls like `setDisableInAppNotifications`
      // can't reliably stop (Gleap's WebSocket push can race past them).
      document.body.classList.add(GLEAP_SILENT_BODY_CLASS);

      try {
        Gleap.setLanguage(UserState.user?.lang ?? 'en');
        Gleap.initialize(process.env.VUE_APP_GLEAP_API_KEY || '');
        Gleap.setMaxNetworkRequests(50);
        Gleap.showFeedbackButton(false);
        Gleap.setDisableInAppNotifications(true);
        // Tear down a banner / modal that was already open before we
        // landed here (e.g. opened earlier by LayoutMain on the V4
        // side, before the V5 redirect).
        Gleap.closeBanner();
        Gleap.closeModal();
      } catch (e) {
        // Gleap init failure shouldn't block the migration
      }

      const waitStepIndex = STEPS.findIndex((s) => s.id === 'wait');
      try {
        const status = await fetchMigrationV5Status();
        const pastConnect = [
          'ready_to_migrate_data',
          'data_migration_in_progress',
          'data_migrated',
        ].includes(status);
        currentIndex.value = pastConnect && waitStepIndex >= 0
          ? waitStepIndex
          : 0;
      } catch (e) {
        currentIndex.value = 0;
      }
    });

    onBeforeUnmount(() => {
      // Restore Gleap defaults so the rest of the V5 app (LayoutMain)
      // keeps its banners and floating button active once the user
      // reaches their dashboard.
      document.body.classList.remove(GLEAP_SILENT_BODY_CLASS);
      try {
        Gleap.showFeedbackButton(true);
        Gleap.setDisableInAppNotifications(false);
      } catch {
        // Gleap may not be loaded — fail silently.
      }
    });

    const narrativeHeadline = computed(() => {
      const id = currentStep.value.id as StepId;
      return t(`migrationV5.narrative.${id}.headline`);
    });
    const narrativeSubtitle = computed(() => {
      const id = currentStep.value.id as StepId;
      return t(`migrationV5.narrative.${id}.subtitle`);
    });

    const handleNext = async () => {
      if (currentStep.value.id === 'connect') {
        // Fire the "ready" signal: backend persists state=ready and
        // sends the Telegram alert to the admin team. Done BEFORE
        // advancing the step so that if the call fails, the user
        // stays on step 1 with a toast and can retry.
        try {
          await notifyMigrationV5Ready();
        } catch (e: any) {
          showToastError(getErrorMessage(e));
          return;
        }
        currentIndex.value += 1;
        return;
      }
      // No-op on the wait step: it auto-advances via window.location.reload()
      // when the migration script finishes (cf. MigrationV5WaitStep).
    };

    const handleBack = () => {
      if (currentIndex.value > 0 && currentStep.value.id !== 'wait') {
        currentIndex.value -= 1;
      }
    };

    const handleLogout = async () => {
      await Logout();
      router.push({ name: 'login' });
    };

    const openSupport = () => {
      try {
        Gleap.open();
      } catch (e) {
        // noop
      }
    };

    return {
      t,
      currentIndex,
      totalSteps,
      currentStep,
      isFirst,
      isLast,
      narrativeHeadline,
      narrativeSubtitle,
      handleNext,
      handleBack,
      handleLogout,
      openSupport,
    };
  },
});
